import React from "react";
import { useDispatch, useSelector } from "react-redux";
import Loader from "components/loader";
import { REMOVE_LINE_ITEM, EDIT_LINE_ITEM, UPDATE_LINE_TO_CREATE, SET_HIGHLIGHT_BANNER } from "utils/action-types";
import handleBeforeUnload from "src/hooks/useConfirmLeave";
import extractIdFromGid from "helpers/extract-gid";
import clsx from "clsx/lite";
import useSaveToast from "hooks/useSaveToast";
import AddToSubscriptionCart from "components/add-to-subscription-cart";
import { useGlobalContext } from "context/global";
import Tooltip from "../tooltip";
import { lifeTimeOfferKey } from "utils/constants";
import obtainOrderInfo from "utils/obtain-order-info";
import { trackAddToSubscriptionCart, trackRemoveFromSubscriptionCart } from "services/analytics";

const SubscriptionQuantityInput = ({
	productVariantId, stock, customButtonClass = "", isInCart, product, onToastClick, addClassName, onRemoved, containerClass, bgColor
}) => {
	const { isActive, lines, draftChanges } = useSelector((state) => state.account.subscription);


	if (!isActive) return null;

	const cartItem = lines[productVariantId] || {};
	const isLineToCreate = draftChanges.linesToCreate[productVariantId];

	handleBeforeUnload(draftChanges.unsaved);

	return (
		<div className={clsx("flex md:justify-center items-center", isInCart ? "mt-5" : "mt-2", containerClass)}>
			{!cartItem?.id && !isLineToCreate ?
				<AddToSubscriptionCart
					productVariantId={productVariantId}
					stock={stock}
					className={addClassName}
					product={product}
					onToastClick={onToastClick}
					backgroundColor={bgColor}
				/> :
				<QuantityControlButtons
					productVariantId={productVariantId}
					stock={stock}
					product={product}
					onToastClick={onToastClick}
					onRemoved={onRemoved}
					isInCart={isInCart}
					customButtonClass={customButtonClass}
				/>}
		</div>

	);
};

const QuantityControlButtons = ({
	productVariantId, stock, onToastClick, product, onRemoved, isInCart, customButtonClass
}) => {
	const {
		isActive, lines, draftChanges, isCartOpen, nextBillingDate, chargeOffsetDays, unavailable_items
	} = useSelector((state) => state.account.subscription);
	const { latestSubOrder } = useSelector(state => state.account.profile);
	const showSaveToast = useSaveToast(onToastClick);
	const { isSubscriptionPage, translation, isCollection } = useGlobalContext();

	const cartItem = lines[productVariantId] || {};
	const isLineToCreate = draftChanges.linesToCreate[productVariantId];
	const isLimittedOffer = cartItem?.attributes?.some(attr => attr.key === lifeTimeOfferKey && attr.value === "true");
	const { isDispatched } = obtainOrderInfo(nextBillingDate, chargeOffsetDays, latestSubOrder);

	const isFree = cartItem.discountedPrice === 0 && +cartItem.price === 0;

	const isUnavailable = stock === "unavailable" ||
		unavailable_items.find(variant => +extractIdFromGid(variant.id) === productVariantId);

	const isIncrementDisabled = isUnavailable || draftChanges.isSyncing || isLimittedOffer || (isFree && (cartItem.id || isLineToCreate));
	const dispatch = useDispatch();

	const quantity = cartItem.isDuplicated && cartItem.duplicates.some(item => item.discountedPrice !== 0) ?
		cartItem.totalQuantity : cartItem.quantity;

	const updateProduct = async (qty = 1) => {
		if (!isActive || draftChanges.isSyncing || (qty > 0 && isIncrementDisabled)) return;

		if (qty < 0) trackRemoveFromSubscriptionCart(product);
		else trackAddToSubscriptionCart(product);

		const modifiedTotalQuantity = quantity + qty;
		const modifiedQuantity = cartItem?.quantity + qty;
		const resetRemoveCount = qty === 1;

		if (modifiedTotalQuantity === 0) {
			onRemoved?.();
		}

		if (isLineToCreate) {
			const existingItem = draftChanges.linesToCreate[productVariantId];

			dispatch({
				type: UPDATE_LINE_TO_CREATE,
				payload: { item: { ...existingItem, quantity: existingItem.quantity + qty }, resetRemoveCount }
			});

		} else {
			const actionType = modifiedQuantity === 0 ? REMOVE_LINE_ITEM : EDIT_LINE_ITEM;

			dispatch({ type: actionType, payload: { item: cartItem, modifiedQuantity, modifiedTotalQuantity, resetRemoveCount } });
		}

		if (!isCartOpen && !isCollection)
			showSaveToast(true);
	};

	const disabledButton = draftChanges.isSyncing || isDispatched;

	return (
		<div onClick={() => isDispatched && dispatch({ type: SET_HIGHLIGHT_BANNER, payload: true })}
			className={clsx("flex gap-4 relative items-center", disabledButton && "opacity-20",
				(isInCart || !isSubscriptionPage) ? "md:mt-2" : "mt-2 bg-brown w-10/12 md:w-full justify-between px-6 rounded-lg")}
			role="button" tabIndex={0}>
			<button
				aria-label="Minus"
				disabled={disabledButton}
				className={
					clsx("plus-minus-button",
						isInCart ? "bg-minus-grey" : isSubscriptionPage ?
							"bg-minus !w-3.5" : "!size-11 md:!w-14 md:!h-8 !rounded-[37px] bg-minus-white",
						customButtonClass, disabledButton && "pointer-events-none"
					)}
				onClick={() => updateProduct(-1)} />
			{cartItem?.updating ? <Loader className="!size-4.5" ring /> :
				<p className={clsx("text-base min-w-[18px] text-center",
					(isInCart || !isSubscriptionPage) ? "text-secondary" : "text-white")} >
					{quantity}
				</p>
			}
			<Tooltip message={translation.subscription?.bundle_limit_offer_message} enabled={isLimittedOffer && !isInCart}>
				<button
					disabled={isIncrementDisabled || disabledButton}
					aria-label="Plus"
					className={clsx("plus-minus-button",
						isInCart ? "bg-plus-grey" : isSubscriptionPage ? "bg-plus !w-3.5" :
							"!size-11 md:!w-14 md:!h-8 !rounded-[37px] bg-plus-black",
						disabledButton && "pointer-events-none", customButtonClass,
						isIncrementDisabled && "opacity-20 cursor-none"
					)}
					onClick={() => updateProduct()} />
			</Tooltip>
		</div>
	);
};

export default SubscriptionQuantityInput;